import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Flex,
  VStack,
  HStack,
  Divider,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Checkbox,
  Collapse,
  Text,
} from '@chakra-ui/react';
import AlarmReportTable from '../components/alarmReportTable.js';
import ExportReportModal from '../components/exportReportModal';
import {
  applyAlarmHistory,
  fetchGroupWithAlarms,
  downloadAlarmReport,
} from '../api/alarm';

const AlarmReportPage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAlarms, setSelectedAlarms] = useState(new Set());
  const [tempSelectedAlarms, setTempSelectedAlarms] = useState(new Set());
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [filteredAlarms, setFilteredAlarms] = useState([]);
  const [alarmGroupsVisibility, setAlarmGroupsVisibility] = useState({});
  const [groupedAlarms, setGroupedAlarms] = useState([]); // Grupları ve alarmları tutar
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    // Grupları ve alarmları almak için API çağrısı
    const fetchData = async () => {
      try {
        const data = await fetchGroupWithAlarms();
        setGroupedAlarms(data, {
          refetchOnWindowFocus: false,
        });
      } catch (error) {
        console.error('Error fetching groups with alarms:', error);
      }
    };

    fetchData();
  }, []);

  const handleCreateReport = async () => {
    // Seçilen alarm ID'lerini listele
    const alarmIds = Array.from(selectedAlarms);

    try {
      // API çağrısını yap
      const response = await applyAlarmHistory(
        new Date(`${startDate}T${startTime}`).toISOString(),
        new Date(`${endDate}T${endTime}`).toISOString(),
        alarmIds,
        pageNumber,
        pageSize
      );

      if (response && response.Items) {
        setFilteredAlarms(response.Items);
        setTotalCount(response.TotalCount);
      } else {
        setFilteredAlarms([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error creating report:', error);
    }
  };

  useEffect(() => {
    setPageNumber(1);
  }, [startDate, startTime, endDate, endTime, selectedAlarms]);

  useEffect(() => {
    if (filteredAlarms.length > 0) {
      handleCreateReport();
    }
  }, [pageNumber, pageSize]);

  const renderPagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    return (
      <Flex mt={4} justifyContent="center" alignItems="center" gap={4}>
        <Button
          onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
          isDisabled={pageNumber === 1}
        >
          Previous
        </Button>
        <Box>
          Page {pageNumber} of {totalPages}
        </Box>
        <Button
          onClick={() => setPageNumber(prev => Math.min(prev + 1, totalPages))}
          isDisabled={pageNumber >= totalPages}
        >
          Next
        </Button>
      </Flex>
    );
  };

  const handleDownloadReport = (fileType, pageRange) => {
    const start = new Date(
      `${startDate}T${startTime || '00:00'}`
    ).toISOString();
    const end = new Date(`${endDate}T${endTime || '23:59'}`).toISOString();
    const alarm_ids = Array.from(selectedAlarms);

    downloadAlarmReport(start, end, alarm_ids, fileType, pageRange);
  };

  const handleClear = () => {
    setSelectedAlarms(new Set());
    setTempSelectedAlarms(new Set());
    setStartDate('');
    setStartTime('');
    setEndDate('');
    setEndTime('');
    setFilteredAlarms([]);
    setAlarmGroupsVisibility({});
  };

  const handlePrint = () => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        .printable-content, .printable-content * {
          visibility: visible;
        }
        .printable-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: auto;
          overflow: visible;
        }
        table {
          width: 100% !important;
          max-width: 100% !important;
          table-layout: fixed !important;
          border-collapse: collapse;
          word-wrap: break-word;
          overflow: hidden !important;
          page-break-inside: auto;
        }
        thead {
          display: table-header-group;
        }
        tbody {
          display: table-row-group;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        th, td {
          padding: 2px !important;
          margin: 0 !important;
          font-size: 11px !important;
          line-height: 1.2 !important;
          border: 1px solid black !important;
          white-space: normal !important;
          word-wrap: break-word !important;
          text-align: left !important;
          overflow: hidden !important;
        }
        th {
          max-width: 200px !important;
          min-width: 50px !important;
        }
        .no-print {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);

    window.print();

    document.head.removeChild(style);
  };

  const handleGroupToggle = name => {
    setAlarmGroupsVisibility(prevState => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const handleAlarmToggle = id => {
    setTempSelectedAlarms(prevState => {
      const newSelectedAlarms = new Set(prevState);
      if (newSelectedAlarms.has(id)) {
        newSelectedAlarms.delete(id);
      } else {
        newSelectedAlarms.add(id);
      }
      return newSelectedAlarms;
    });
  };

  const handleSave = () => {
    setSelectedAlarms(tempSelectedAlarms);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedAlarms(selectedAlarms);
    onClose();
  };
  const handleExport = () => {
    setIsExportModalOpen(true);
  };
  const handleSelectAll = () => {
    const allAlarms = new Set(
      groupedAlarms.flatMap(group => group.group.alarms.map(alarm => alarm.id))
    );
    setTempSelectedAlarms(allAlarms);
  };

  const handleUnselectAll = () => {
    setTempSelectedAlarms(new Set());
  };

  return (
    <Flex direction="column" w="full" p={4}>
      <Box bg="gray.100" p={4} rounded="md" w="full">
        <Flex justify="space-between" wrap="wrap" align="center" w="full">
          <HStack spacing={4} align="flex-start" w="full">
            <HStack spacing={2}>
              <Text>Alarm:</Text>
              <Button colorScheme="blue" onClick={onOpen} minW="200px">
                Select
              </Button>
            </HStack>
            <FormControl flex="1">
              <FormLabel>Start Date</FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  if (!startTime) setStartTime('00:00');
                }}
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel>Start Time</FormLabel>
              <Input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel>End Date</FormLabel>
              <Input
                type="date"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  if (!endTime) setEndTime('23:59');
                }}
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel>End Time</FormLabel>
              <Input
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
              />
            </FormControl>
            <VStack align="flex-start" spacing={2}>
              <Button
                bg="gray.700"
                color="white"
                onClick={handleCreateReport}
                w="full"
              >
                Apply
              </Button>
              <Button
                bg="gray.700"
                color="white"
                onClick={handleClear}
                w="full"
              >
                Clear
              </Button>
              <Button
                bg="gray.700"
                color="white"
                onClick={handleExport}
                w="full"
              >
                Export
              </Button>
            </VStack>
          </HStack>
        </Flex>
      </Box>
      <Divider my={4} />
      <Box flex="1">
        <Box flex="1">
          {!filteredAlarms.length ? (
            <Box>No data to display</Box>
          ) : (
            <>
              <AlarmReportTable data={filteredAlarms} />
              {renderPagination()}
            </>
          )}
        </Box>
      </Box>
      <Button
        position="fixed"
        bottom="20px"
        right="20px"
        colorScheme="green"
        onClick={handlePrint}
      >
        Print
      </Button>

      <Modal isOpen={isOpen} onClose={handleCancel} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Alarm and Group</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="flex-start" spacing={4}>
              {groupedAlarms
                .filter(
                  group => group.group.alarms && group.group.alarms.length > 0
                ) // Boş olmayan grupları filtrele
                .map(group => (
                  <Box key={group.group.id}>
                    <Button
                      onClick={() => handleGroupToggle(group.group.name)}
                      variant="link"
                      leftIcon={<Box as="span">+</Box>}
                    >
                      {group.group.name}
                    </Button>
                    <Collapse in={alarmGroupsVisibility[group.group.name]}>
                      <VStack align="flex-start" pl={4}>
                        {group.group.alarms.map(alarm => (
                          <Checkbox
                            key={alarm.id}
                            isChecked={tempSelectedAlarms.has(alarm.id)}
                            onChange={() => handleAlarmToggle(alarm.id)}
                          >
                            {alarm.name}
                          </Checkbox>
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                ))}
            </VStack>
            <Text as="u" cursor="pointer" onClick={handleSelectAll} mt={4}>
              Select All
            </Text>
            <br></br>
            <Text as="u" cursor="pointer" onClick={handleUnselectAll} mt={2}>
              Unselect All
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ExportReportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onDownload={handleDownloadReport}
      />
    </Flex>
  );
};

export default AlarmReportPage;
